import { Injectable, inject } from '@angular/core';
import { Observable, switchMap, map, catchError, throwError, of } from 'rxjs';
import { FirebaseAuthService } from './firebase-auth.service';
import { JwtBackendService } from './jwt-backend.service';
import { AuthenticationService, LoginResponse, RegisterResponse } from '../port/authentication.service';
import { EmailAlreadyTakenError } from '@app/visitor/signup/domain/email-already-taken.error';
import { InvalidCredentialError } from '@app/visitor/login/domain/invalid-credential.error';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationOrchestratorService implements AuthenticationService {
  private readonly firebaseAuth = inject(FirebaseAuthService);
  private readonly jwtBackend = inject(JwtBackendService);

  /**
   * Inscription complète : Firebase + JWT Backend
   */
  register(email: string, password: string): Observable<RegisterResponse> {
    if (!environment.production) {
      console.log('🎭 ORCHESTRATOR: Début inscription complète');
    }
    
    return this.firebaseAuth.signup(email, password).pipe(
      switchMap((firebaseResponse) => {
        // Échanger le token Firebase contre un JWT backend
        return this.jwtBackend.exchangeFirebaseToken(firebaseResponse.idToken).pipe(
          map((jwtResponse) => {
            // Stocker les tokens
            this.jwtBackend.storeTokens(jwtResponse.token, firebaseResponse.refreshToken);
            
            if (!environment.production) {
              console.log('✅ ORCHESTRATOR: Inscription complète réussie');
            }
            return {
              jwtToken: jwtResponse.token,
              jwtRefreshToken: firebaseResponse.refreshToken,
              expiresIn: firebaseResponse.expiresIn,
              userId: firebaseResponse.localId,
            };
          })
        );
      }),
      catchError((error) => {
        if (error.error?.error?.message === 'EMAIL_EXISTS') {
          return of(new EmailAlreadyTakenError(email));
        }
        console.error('❌ ORCHESTRATOR: Erreur inscription:', error);
        return throwError(() => new Error('Error during registration'));
      })
    );
  }

  /**
   * Connexion complète : Firebase + JWT Backend
   */
  login(email: string, password: string): Observable<LoginResponse> {
    if (!environment.production) {
      console.log('🎭 ORCHESTRATOR: Début connexion complète');
    }
    
    return this.firebaseAuth.signin(email, password).pipe(
      switchMap((firebaseResponse) => {
        // Échanger le token Firebase contre un JWT backend
        return this.jwtBackend.exchangeFirebaseToken(firebaseResponse.idToken).pipe(
          map((jwtResponse) => {
            // Stocker les tokens
            this.jwtBackend.storeTokens(jwtResponse.token, firebaseResponse.refreshToken);
            
            if (!environment.production) {
              console.log('✅ ORCHESTRATOR: Connexion complète réussie');
            }
            return {
              jwtToken: jwtResponse.token,
              jwtRefreshToken: firebaseResponse.refreshToken,
              expiresIn: firebaseResponse.expiresIn,
              userId: firebaseResponse.localId,
              isRegistered: firebaseResponse.registered,
            };
          })
        );
      }),
      catchError((error) => {
        if (error.error?.error?.message === 'INVALID_LOGIN_CREDENTIALS') {
          return of(new InvalidCredentialError());
        }
        console.error('❌ ORCHESTRATOR: Erreur connexion:', error);
        return throwError(() => new Error('Error during login'));
      })
    );
  }

  /**
   * Refresh complet : Firebase + JWT Backend
   */
  refreshToken(refreshToken: string): Observable<{ jwtToken: string; userId: string }> {
    if (!environment.production) {
      console.log('🎭 ORCHESTRATOR: Début refresh complet');
    }
    
    return this.firebaseAuth.refreshToken(refreshToken).pipe(
      switchMap((firebaseResponse) => {
        // Échanger le nouveau token Firebase contre un nouveau JWT
        return this.jwtBackend.exchangeFirebaseToken(firebaseResponse.id_token).pipe(
          map((jwtResponse) => {
            // Stocker le nouveau JWT
            this.jwtBackend.storeTokens(jwtResponse.token, firebaseResponse.refresh_token);
            
            if (!environment.production) {
              console.log('✅ ORCHESTRATOR: Refresh complet réussi');
            }
            return {
              jwtToken: jwtResponse.token,
              userId: firebaseResponse.user_id,
            };
          })
        );
      }),
      catchError((error) => {
        console.error('❌ ORCHESTRATOR: Erreur refresh:', error);
        // En cas d'erreur, nettoyer les tokens
        this.jwtBackend.clearStoredTokens();
        return throwError(() => new Error('Error refreshing token'));
      })
    );
  }

  /**
   * Auto-connect intelligent
   */
  autoConnect(): Observable<any | null> {
    if (!environment.production) {
      console.log('🎭 ORCHESTRATOR: Auto-connect intelligent');
    }
    
    // 1. Vérifier si on a un JWT valide
    if (this.jwtBackend.isStoredJwtValid()) {
      if (!environment.production) {
        console.log('✅ ORCHESTRATOR: JWT valide trouvé');
      }
      
      // Vérifier si le JWT expire bientôt
      if (this.jwtBackend.isStoredJwtExpiringSoon(300)) {
        if (!environment.production) {
          console.log('⏰ ORCHESTRATOR: JWT expire bientôt, refresh...');
        }
        const refreshToken = this.jwtBackend.getStoredRefreshToken();
        if (refreshToken) {
          return this.refreshToken(refreshToken).pipe(
            switchMap(() => this.loadCurrentUser()),
            catchError(() => of(null))
          );
        }
      }
      
      // JWT valide, charger l'utilisateur
      return this.loadCurrentUser();
    }

    // 2. Pas de JWT valide
    if (!environment.production) {
      console.log('❌ ORCHESTRATOR: Pas de JWT valide');
    }
    this.jwtBackend.clearStoredTokens();
    return of(null);
  }

  /**
   * Récupère le token JWT actuel
   */
  getToken(): string | null {
    const token = this.jwtBackend.getStoredJwt();
    if (!environment.production) {
      console.log('🔑 ORCHESTRATOR: getToken appelé, token:', token ? 'EXISTE' : 'NULL');
    }
    return token;
  }

  /**
   * Déconnexion complète
   */
  logout(): void {
    if (!environment.production) {
      console.log('🎭 ORCHESTRATOR: Déconnexion complète');
    }
    this.firebaseAuth.signout();
    this.jwtBackend.clearStoredTokens();
  }

  /**
   * Charge les données de l'utilisateur actuel
   */
  private loadCurrentUser(): Observable<any> {
    const jwt = this.jwtBackend.getStoredJwt();
    if (!jwt) return of(null);
    
    return this.jwtBackend.getCurrentUser(jwt).pipe(
      catchError((error) => {
        console.error('❌ ORCHESTRATOR: Erreur chargement utilisateur:', error);
        this.jwtBackend.clearStoredTokens();
        return of(null);
      })
    );
  }
}